import { useState, useRef } from 'react'
import { profileService } from '../../services/profileService'
import WheelLoader from '../common/WheelLoader'
import type { Profile } from '../../types'

interface ProfileImageUploadProps {
  imageUrl: string | null
  name: string
  onUpdate: (profile: Profile) => void
}

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
const MAX_SIZE_MB = 5
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export default function ProfileImageUpload({ imageUrl, name, onUpdate }: ProfileImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Reset input
    e.target.value = ''

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Please upload a JPEG, PNG, GIF, or WebP image')
      return
    }

    // Validate file size
    if (file.size > MAX_SIZE_BYTES) {
      setError(`Image must be less than ${MAX_SIZE_MB}MB`)
      return
    }

    setError(null)
    setIsUploading(true)

    try {
      const updatedProfile = await profileService.uploadProfileImage(file)
      onUpdate(updatedProfile)
    } catch {
      setError('Failed to upload image. Please try again.')
    } finally {
      setIsUploading(false)
    }
  }

  const handleRemove = async () => {
    setError(null)
    setIsRemoving(true)

    try {
      const updatedProfile = await profileService.removeProfileImage()
      onUpdate(updatedProfile)
    } catch {
      setError('Failed to remove image. Please try again.')
    } finally {
      setIsRemoving(false)
    }
  }

  const isLoading = isUploading || isRemoving

  return (
    <div className="mb-6 pb-6 border-b border-gray-100">
      <label className="block text-sm font-medium text-gray-700 mb-3">Profile Photo</label>

      <div className="flex items-center gap-4">
        {/* Avatar */}
        <div className="relative group">
          <button
            type="button"
            onClick={handleClick}
            disabled={isLoading}
            className="relative w-24 h-24 rounded-full overflow-hidden bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {imageUrl ? (
              <img src={imageUrl} alt={name} className="w-full h-full object-cover" />
            ) : (
              <span className="flex items-center justify-center w-full h-full text-2xl font-semibold text-indigo-600">
                {getInitials(name)}
              </span>
            )}

            {/* Hover overlay */}
            {!isLoading && (
              <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
            )}

            {/* Loading spinner */}
            {isLoading && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                <WheelLoader width={36} />
              </div>
            )}
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={handleClick}
            disabled={isLoading}
            className="px-3 py-1.5 text-sm text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading ? 'Uploading...' : 'Change photo'}
          </button>

          {imageUrl && (
            <button
              type="button"
              onClick={handleRemove}
              disabled={isLoading}
              className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRemoving ? 'Removing...' : 'Remove'}
            </button>
          )}
        </div>
      </div>

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

      <p className="mt-2 text-xs text-gray-500">JPEG, PNG, GIF, or WebP. Max {MAX_SIZE_MB}MB.</p>
    </div>
  )
}
